/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { AdcPlatformModel, AdcServicesModel, SuppliersModel } from '../../../../shared/models';
import { BaseComponent, ProgressBarComponent } from '../../../../../../core/components';
import { Component, ComponentRef, OnInit, ViewChildren, ViewContainerRef } from '@angular/core';
import { DownloadService, LogService, UtilsService } from '../../../../../../core/services';
import { EndurancePlatforms, EnterprisePlatforms, ShuttleSuppliers } from '../../../../shared/lists';
import { HttpEventType } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-downloads',
    templateUrl: './select-platform.component.html',
    styleUrls: ['./select-platform.component.scss']
})
export class SelectPlatformComponent extends BaseComponent implements OnInit {
    /**
     * Reference to new ADC container
     */
    @ViewChildren('progressContainer', { read: ViewContainerRef })
    public progressContainer: any;

    /**
     * Store the select shuttle details.
     */
    public selectedShuttle: SuppliersModel;

    /**
     * File location for downloads.
     */
    public fileDownloadUrl: string = 'https://downloads.loadbalancer.org/releases/virtual/current/';

    /**
     * Store the selected simple download option names.
     */
    public downloadedList: string[] = [];

    /**
     * List of suppliers.
     */
    public platformRows: AdcPlatformModel[] = [];

    /**
     * Store content click.
     */
    public clickedItemTitle: string;

    /**
     * Modal Component Reference
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private downloadService: DownloadService,
        private logService: LogService,
        private route: ActivatedRoute,
        private utilsService: UtilsService
    ) {
        super();

        const shuttleName = this.route.snapshot.paramMap.get('shuttleName');
        this.selectedShuttle = ShuttleSuppliers.find(item => item.name === shuttleName);
    }

    ngOnInit(): void {
        this.populateSupplierRows();
    }

    /**
     * Temp link to website until tracking is in place.
     */
    public tempLinkToWebsite(): void {
        window.open('https://www.loadbalancer.org/get-started/adc-free-trial/', '_blank');
    }

    /**
     * Trigger a simple download.
     * @param platform
     * @param service
     */
    public triggerSimpleDownload(platform: AdcPlatformModel, service: AdcServicesModel): void {
        const fileUrl = this.fileDownloadUrl + service.fileName;
        const referenceName = platform.name + '-' + service.fileName;
        this.downloadedList.push(referenceName);
        this.downloadService.download(fileUrl, 'blob')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (result: any): void => {
                    if (result.type === HttpEventType.Response) {
                        this.downloadService.generateDownload(
                            result.body, fileUrl, { callBack: this.clearDownloadedList.bind(this, referenceName) }
                        );
                    }
                },
                error: (error: any): void => {
                    this.logService.error('AddLoadbalancerComponent: triggerSimpleDownload', error);
                }
            });
    }

    /**
     * Trigger the download in the download component.
     * @param platform
     * @param i
     */
    public triggerDownload(platform: any, i: number): void {
        const service = platform.services[0];
        this.clickedItemTitle = platform.title;
        const fileUrl: string = this.fileDownloadUrl + service.fileName;
        const containers = this.progressContainer.toArray();
        const progressComponent = containers[i].createComponent(ProgressBarComponent);
        // Set value in the progress component.
        progressComponent.instance.fileUrl = fileUrl;
        progressComponent.instance.spinner = true;
        progressComponent.instance.onCompleteCallBack = this.cleanUpProgressComponent.bind(this);
        progressComponent.instance.onCancelCallBack = this.cleanUpProgressComponent.bind(this);
        // Component reference for destroy later.
        this.componentReference = progressComponent;
    }

    /**
     * Clean up progress component.
     * @private
     */
    private cleanUpProgressComponent(): void {
        this.clickedItemTitle = null;
        if (this.componentReference) {
            this.componentReference.destroy();
        }
    }

    /**
     * Clear download list.
     * @param fileName
     * @private
     */
    private clearDownloadedList(fileName: string): void {
        const deleteIndex: number = this.downloadedList.findIndex(item => item === fileName);
        if (deleteIndex !== -1) {
            const timer = setTimeout(() => {
                this.downloadedList.splice(deleteIndex);
                clearTimeout(timer);
            }, 1500); // <-- Note there is an animation with the same duration in the scss.
        }
    }

    /**
     * Populate supplier rows
     * @private
     */
    private populateSupplierRows(): void {
        const supplierRows: AdcPlatformModel[] = this.getSupplierRows();
        if (supplierRows.length) {
            const rows: any[] = [];
            for (const supplier of supplierRows) {
                rows.push(supplier);
            }

            this.platformRows = this.utilsService.sortArrayByProperty(rows, 'active', [true, false]);
        }
    }

    /**
     * Get supplier rows based on selected shuttle.
     * @private
     */
    private getSupplierRows(): AdcPlatformModel[] {
        switch (this.selectedShuttle?.name) {
            case 'lbShuttleEnterprise':
                return EnterprisePlatforms;
            case 'lbShuttleEndurance':
                return EndurancePlatforms;
            default:
                return [];
        }
    }
}
